/**
 * OtpSheet — reusable inline OTP verification bottom-sheet.
 *
 * Props:
 *   open: boolean
 *   onOpenChange(next)
 *   phone: 10-digit (or country-appropriate) string already collected
 *   countryCode: e.g. "+91"
 *   devOtp?: string  — when REAL_SMS_ENABLED=false, the backend echoes the
 *                      OTP in the start response; show it as a dev hint.
 *   resendStart(): Promise<{dev_otp?:string}> — re-fires /auth/otp/start
 *   onVerified({access_token, refresh_token, buyer}): called on success
 *   onEditPhone(): close sheet and let parent focus phone field
 *   submitLabel?: string  — defaults to "Verify"
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "../ui/drawer";
import { Loader2 } from "lucide-react";
import { toast } from "../ui/sonner";
import { verifyOtp, persistAuth } from "../../lib/api";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;

function formatPhoneMask(cc, phone) {
  const last10 = (phone || "").replace(/\D/g, "").slice(-10);
  if (last10.length === 10) {
    return `${cc} ${last10.slice(0, 5)} ${last10.slice(5)}`;
  }
  return `${cc} ${phone}`;
}

export default function OtpSheet({
  open,
  onOpenChange,
  phone,
  countryCode = "+91",
  devOtp,
  resendStart,
  onVerified,
  onEditPhone,
  submitLabel = "Verify",
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const [currentDevOtp, setCurrentDevOtp] = useState(devOtp || null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    setCurrentDevOtp(devOtp || null);
  }, [devOtp]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setCode("");
      setErrorMessage(null);
      setResendIn(RESEND_COOLDOWN_S);
      setTimeout(() => firstInputRef.current?.focus(), 80);
    }
  }, [open]);

  // Countdown
  useEffect(() => {
    if (!open) return undefined;
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open, resendIn]);

  const phoneLabel = useMemo(() => formatPhoneMask(countryCode, phone), [countryCode, phone]);

  const submit = async () => {
    if (code.length !== OTP_LENGTH || !code.match(/^\d{6}$/)) {
      setErrorMessage("Enter the 6-digit code.");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const data = await verifyOtp({ phone, code, country_code: countryCode });
      persistAuth(data);
      onVerified?.(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || (status === 429 ? "Too many attempts. Try later." : "Couldn't verify. Try again.");
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setErrorMessage(null);
    try {
      const data = (await resendStart?.()) || {};
      if (data.dev_otp) setCurrentDevOtp(data.dev_otp);
      setCode("");
      setResendIn(RESEND_COOLDOWN_S);
      toast.success("New code sent.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : detail?.message || "Couldn't resend. Try again.";
      setErrorMessage(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="otp-sheet" className="bg-[#FAF7F2]">
        <div className="mx-auto w-full max-w-md px-5 pb-8 pt-2 sm:px-6">
          <DrawerHeader className="px-0 text-left">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
              Verify your phone
            </p>
            <DrawerTitle className="font-display text-3xl text-[#1A1A1A]">
              Enter the 6-digit code
            </DrawerTitle>
            <DrawerDescription className="text-sm text-[#1A1A1A]/65">
              We sent it to{" "}
              <span className="font-medium text-[#1A1A1A]">{phoneLabel}</span>.{" "}
              <button
                type="button"
                data-testid="otp-edit-phone"
                className="underline decoration-[#C8A24B] underline-offset-4 hover:text-[#1A1A1A]"
                onClick={() => onEditPhone?.()}
              >
                Edit number
              </button>
            </DrawerDescription>
          </DrawerHeader>

          <OtpBoxes
            value={code}
            onChange={setCode}
            firstInputRef={firstInputRef}
            disabled={submitting}
            onComplete={submit}
          />

          {currentDevOtp ? (
            <p
              data-testid="otp-dev-hint"
              className="mt-3 text-center text-[11px] text-[#1A1A1A]/55"
            >
              <span className="rounded-full bg-[#1A1A1A]/5 px-2 py-1 font-mono tracking-widest text-[#1A1A1A]/75">
                Dev OTP: {currentDevOtp}
              </span>{" "}
              <span className="ml-1">(REAL_SMS_ENABLED=false)</span>
            </p>
          ) : null}

          {errorMessage ? (
            <p
              data-testid="otp-error"
              role="alert"
              className="mt-3 text-center text-xs text-[#B0413E]"
            >
              {errorMessage}
            </p>
          ) : null}

          <button
            type="button"
            data-testid="otp-verify-button"
            disabled={submitting || code.length !== OTP_LENGTH}
            onClick={submit}
            className={[
              "mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em] transition",
              submitting || code.length !== OTP_LENGTH
                ? "cursor-not-allowed bg-[#1A1A1A]/25 text-[#FAF7F2]"
                : "bg-[#1A1A1A] text-[#FAF7F2] hover:bg-[#2a2a2a]",
            ].join(" ")}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitLabel}
          </button>

          <div className="mt-5 text-center text-xs text-[#1A1A1A]/55">
            {resendIn > 0 ? (
              <span data-testid="otp-resend-countdown">
                Resend in <span className="font-medium text-[#1A1A1A]">{resendIn}s</span>
              </span>
            ) : (
              <button
                type="button"
                data-testid="otp-resend-button"
                disabled={resending}
                onClick={resend}
                className="font-medium text-[#1A1A1A] underline decoration-[#C8A24B] underline-offset-4 hover:text-[#C8A24B]"
              >
                {resending ? "Sending…" : "Resend code"}
              </button>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export function OtpBoxes({ value, onChange, firstInputRef, disabled, onComplete }) {
  const digits = value.padEnd(OTP_LENGTH, " ").slice(0, OTP_LENGTH).split("");
  const inputsRef = useRef([]);

  const handleChange = (idx, raw) => {
    const v = (raw || "").replace(/\D/g, "");
    if (!v) {
      const next = value.split("");
      next[idx] = "";
      onChange(next.join("").trim());
      return;
    }
    // If multiple digits pasted, distribute
    if (v.length > 1) {
      const filled = (value.slice(0, idx) + v).slice(0, OTP_LENGTH);
      onChange(filled);
      const target = Math.min(filled.length, OTP_LENGTH - 1);
      inputsRef.current[target]?.focus();
      if (filled.length === OTP_LENGTH) onComplete?.();
      return;
    }
    const next = (value + v).slice(0, OTP_LENGTH);
    // overwrite at idx if user is rewriting
    const arr = value.split("");
    arr[idx] = v.slice(-1);
    const merged = arr.join("").slice(0, OTP_LENGTH);
    onChange(merged);
    if (idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
    if (merged.length === OTP_LENGTH && !merged.includes("")) onComplete?.();
  };

  const handleKey = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx].trim() && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
      const arr = value.split("");
      arr[idx - 1] = "";
      onChange(arr.join(""));
    }
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1)
      inputsRef.current[idx + 1]?.focus();
  };

  return (
    <div
      data-testid="otp-input-row"
      className="mt-6 flex items-center justify-between gap-2 sm:gap-3"
      onPaste={(e) => {
        const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (txt) {
          e.preventDefault();
          onChange(txt);
          inputsRef.current[Math.min(txt.length - 1, OTP_LENGTH - 1)]?.focus();
          if (txt.length === OTP_LENGTH) onComplete?.();
        }
      }}
    >
      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
            if (i === 0 && firstInputRef) firstInputRef.current = el;
          }}
          data-testid={`otp-digit-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={digits[i].trim()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          className="h-12 w-10 rounded-2xl border border-[#1A1A1A]/15 bg-white text-center font-display text-2xl text-[#1A1A1A] outline-none transition focus:border-[#C8A24B] focus:ring-2 focus:ring-[#C8A24B]/20 sm:h-14 sm:w-12"
          aria-label={`Digit ${i + 1}`}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}
